import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as messagesApi from '../api/messages';

// Le chat arrivano a pagine e ricerca e filtri li applica il server: con
// migliaia di chat l'elenco intero pesava 650 KB a ogni aggiornamento.
export const PAGINA_CHAT = 50;

// Lo stesso ordine del server, per ultima attività; a parità resta l'ordine in
// cui sono arrivate (l'ordinamento è stabile). Unendo la prima pagina fresca con
// le chat già caricate, quelle sotto restavano nell'ordine vecchio e durante la
// sincronizzazione chat ferme da mesi finivano sopra a quelle di oggi.
export function perAttivita(a, b) {
  const ta = a.last_activity ? Date.parse(a.last_activity) : -Infinity;
  const tb = b.last_activity ? Date.parse(b.last_activity) : -Infinity;
  return (tb - ta) || 0;
}

const pagina = data => (Array.isArray(data) ? { results: data, next: null } : data);
const chiave = query => JSON.stringify(query);

/** Prima pagina. `reset` quando cambiano ricerca o filtri; senza, aggiorna in testa. */
export const fetchContacts = createAsyncThunk('messages/fetchContacts', async ({ reset = false } = {}, { getState }) => {
  const query = getState().messages.contactsQuery;
  const { data } = await messagesApi.listContacts({ ...query, limit: PAGINA_CHAT, offset: 0 });
  return { ...pagina(data), reset, query: chiave(query) };
});

export const fetchMoreContacts = createAsyncThunk('messages/fetchMoreContacts', async (_, { getState }) => {
  const { contactsQuery, contactsNext } = getState().messages;
  const { data } = await messagesApi.listContacts({ ...contactsQuery, limit: PAGINA_CHAT, offset: contactsNext });
  return { ...pagina(data), query: chiave(contactsQuery) };
}, {
  condition: (_, { getState }) => {
    const { contactsNext, contactsLoadingMore } = getState().messages;
    return contactsNext != null && !contactsLoadingMore;
  },
});

export const fetchMessages = createAsyncThunk('messages/fetch', async (contactId) => {
  const { data } = await messagesApi.listMessages(contactId);
  return { contactId, messages: data };
});

export const fetchOlderMessages = createAsyncThunk('messages/older', async ({ contactId, before }) => {
  const { data } = await messagesApi.listMessages(contactId, before);
  return { contactId, messages: data };
});

export const mergeMessages = (current, updates) => {
  const merged = new Map(current.map(message => [message.id, message]));
  updates.forEach(message => merged.set(message.id, message));
  return Array.from(merged.values()).sort((a, b) => (Date.parse(b.timestamp) - Date.parse(a.timestamp)) || b.id - a.id);
};

export const sendMessageAsync = createAsyncThunk('messages/send', async ({ sessionId, chatId, message }) => {
  const { data } = await messagesApi.sendMessage(sessionId, chatId, message);
  return { ...data, connection: sessionId, chatId };
});

export const sendMediaAsync = createAsyncThunk('messages/sendMedia', async ({ sessionId, chatId, file, caption }) => {
  const { data } = await messagesApi.sendMedia(sessionId, chatId, file, caption);
  return { ...data, connection: sessionId, chatId };
});

const messageSlice = createSlice({
  name: 'messages',
  initialState: {
    contacts: [],
    contactsQuery: { q: '', connection: 'connected', kind: 'all' },
    contactsNext: null,
    contactsLoadingMore: false,
    contactsLoading: true,
    contactsError: '',
    messages: [],
    selectedContact: null,
    loading: false,
    contactsRequest: null,
    messagesRequest: null,
    olderRequest: null,
    hasOlder: false,
  },
  reducers: {
    setContactsQuery(state, { payload }) {
      state.contactsQuery = { ...state.contactsQuery, ...payload };
    },
    selectContact(state, { payload }) {
      state.selectedContact = payload;
      state.messages = [];
      state.messagesRequest = null;
      state.olderRequest = null;
      state.hasOlder = false;
      state.loading = false;
    },
    addIncomingMessage(state, { payload }) {
      if (payload.connection === state.selectedContact?.connection &&
          (payload.contact === state.selectedContact?.id || payload.chatId === state.selectedContact?.whatsapp_id)) {
        state.messages = mergeMessages(state.messages, [payload]);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContacts.pending, (state, action) => { state.contactsRequest = action.meta.requestId; state.contactsLoading = true; })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        if (state.contactsRequest !== action.meta.requestId) return;
        const { results, next, reset, query } = action.payload;
        state.contactsLoading = false; state.contactsError = '';
        if (reset || query !== chiave(state.contactsQuery)) {
          state.contacts = results; state.contactsNext = next;
        } else {
          // Aggiornamento: la prima pagina in testa, sotto le chat già caricate.
          const nuove = new Set(results.map(c => c.id));
          state.contacts = [...results, ...state.contacts.filter(c => !nuove.has(c.id))].sort(perAttivita);
          if (state.contactsNext == null && next != null && state.contacts.length <= PAGINA_CHAT) state.contactsNext = next;
        }
      })
      .addCase(fetchMoreContacts.pending, state => { state.contactsLoadingMore = true; })
      .addCase(fetchMoreContacts.fulfilled, (state, action) => {
        state.contactsLoadingMore = false;
        if (action.payload.query !== chiave(state.contactsQuery)) return;
        const presenti = new Set(state.contacts.map(c => c.id));
        state.contacts.push(...action.payload.results.filter(c => !presenti.has(c.id)));
        state.contacts.sort(perAttivita);
        state.contactsNext = action.payload.next;
      })
      .addCase(fetchMoreContacts.rejected, state => { state.contactsLoadingMore = false; })
      .addCase(fetchContacts.rejected, (state, action) => {
        if (state.contactsRequest === action.meta.requestId) { state.contactsLoading = false; state.contactsError = 'Impossibile caricare le chat. Riprova tra qualche secondo.'; }
      })
      .addCase(fetchMessages.pending, (state, action) => {
        if (state.selectedContact?.id !== action.meta.arg) return;
        state.loading = true; state.messagesRequest = action.meta.requestId;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        if (state.selectedContact?.id !== action.payload.contactId || state.messagesRequest !== action.meta.requestId) return;
        state.loading = false;
        const updates = action.payload.messages;
        const overlap = updates.some(message => state.messages.some(old => old.id === message.id));
        if (!state.messages.length || (updates.length === 100 && !overlap)) {
          // A long outage can leave a gap larger than a page. Restart pagination
          // at the current head instead of silently skipping the missing range.
          state.hasOlder = updates.length === 100;
          state.messages = mergeMessages([], updates);
          state.olderRequest = null;
        } else state.messages = mergeMessages(state.messages, updates);
      })
      .addCase(fetchOlderMessages.pending, (state, action) => {
        if (state.selectedContact?.id === action.meta.arg.contactId) state.olderRequest = action.meta.requestId;
      })
      .addCase(fetchOlderMessages.fulfilled, (state, action) => {
        if (state.selectedContact?.id !== action.payload.contactId || state.olderRequest !== action.meta.requestId) return;
        state.olderRequest = null;
        state.hasOlder = action.payload.messages.length === 100;
        state.messages = mergeMessages(state.messages, action.payload.messages);
      })
      .addCase(fetchOlderMessages.rejected, (state, action) => {
        if (state.olderRequest === action.meta.requestId) state.olderRequest = null;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        if (state.messagesRequest === action.meta.requestId) state.loading = false;
      })
      .addCase(sendMessageAsync.fulfilled, (state, { payload }) => {
        if (payload.connection === state.selectedContact?.connection &&
          (payload.contact === state.selectedContact?.id || payload.chatId === state.selectedContact?.whatsapp_id)) {
        if (!state.messages.some(m => m.id === payload.id)) state.messages.unshift(payload);
      }
      })
      .addCase(sendMediaAsync.fulfilled, (state, { payload }) => {
        if (payload.connection === state.selectedContact?.connection &&
          (payload.contact === state.selectedContact?.id || payload.chatId === state.selectedContact?.whatsapp_id)) {
        if (!state.messages.some(m => m.id === payload.id)) state.messages.unshift(payload);
      }
      })
      .addCase('auth/logout/fulfilled', () => messageSlice.getInitialState())
      .addCase('auth/loadUser/rejected', () => messageSlice.getInitialState());
  },
});

export const { selectContact, addIncomingMessage, setContactsQuery } = messageSlice.actions;
export default messageSlice.reducer;
